import { compileShader, linkProgram } from './gl-program.helpers';

export type PointsGlResources = {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  aPosition: number;
  aCorner: number;
  aFill: number;
  aStroke: number;
  aOpacity: number;
  aSize: number;
  aStrokeScale: number;
  uMatrix: WebGLUniformLocation;
  uResolution: WebGLUniformLocation;
  uPixelRatio: WebGLUniformLocation;
  uStrokeWidth: WebGLUniformLocation;
};

/**
 * Initialize WebGL program + buffer for point rendering.
 * @param gl WebGL context provided by Mapbox custom layer
 */
export function initPointsGl(gl: WebGLRenderingContext): PointsGlResources {
  const vsSource = `
    precision highp float;
    attribute vec3 a_pos;
    attribute vec2 a_corner;
    attribute vec3 a_fill;
    attribute vec3 a_stroke;
    attribute float a_opacity;
    attribute float a_size; // diameter in CSS px
    attribute float a_stroke_scale; // e.g. 3 for selected point ring

    uniform mat4 u_matrix;
    uniform vec2 u_resolution;
    uniform float u_pixel_ratio;

    varying vec2 v_corner;
    varying vec3 v_fill;
    varying vec3 v_stroke;
    varying float v_opacity;
    varying float v_size_px;
    varying float v_stroke_scale;

    void main() {
      v_corner = a_corner;
      v_fill = a_fill;
      v_stroke = a_stroke;
      v_opacity = a_opacity;
      v_stroke_scale = a_stroke_scale;
      // Henrik: keep sizes configured in CSS px, but convert to device px for the Mapbox canvas.
      v_size_px = a_size * u_pixel_ratio;

      vec4 clip_pos = u_matrix * vec4(a_pos, 1.0);
      vec2 screen_pos = clip_pos.xy / clip_pos.w;

      // Screen-aligned quad in pixel space.
      // Henrik: interpret a_size as final diameter in CSS px (scaled by u_pixel_ratio).
      vec2 offset = a_corner * (v_size_px / u_resolution);
      gl_Position = vec4((screen_pos + offset) * clip_pos.w, clip_pos.z, clip_pos.w);
    }
  `;

  const fsSource = `
    precision mediump float;
    uniform float u_stroke_width; // device px (base; multiplied by v_stroke_scale per point)

    varying vec2 v_corner;
    varying vec3 v_fill;
    varying vec3 v_stroke;
    varying float v_opacity;
    varying float v_size_px;
    varying float v_stroke_scale;

    void main() {
      float d = length(v_corner);
      if (d > 1.0) {
        discard;
      }

      // Henrik: simple, stable styling - fill + stroke without extra textures.
      vec3 fill = v_fill;

      // Stroke width in screen space; scale per point (e.g. 3x for selected ring).
      float radiusPx = max(v_size_px * 0.5, 1.0);
      float effectiveStrokePx = u_stroke_width * v_stroke_scale;
      float strokeNorm = clamp(effectiveStrokePx / radiusPx, 0.0, 1.0);
      float isStroke = step(1.0 - strokeNorm, d);
      vec3 rgb = mix(fill, v_stroke, isStroke);

      gl_FragColor = vec4(rgb, v_opacity);
    }
  `;

  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = linkProgram(gl, vs, fs);

  const buffer = gl.createBuffer();
  if (!buffer) {
    gl.deleteProgram(program);
    throw new Error('Failed to create WebGL buffer for points');
  }

  const aPosition = gl.getAttribLocation(program, 'a_pos');
  const aCorner = gl.getAttribLocation(program, 'a_corner');
  const aFill = gl.getAttribLocation(program, 'a_fill');
  const aStroke = gl.getAttribLocation(program, 'a_stroke');
  const aOpacity = gl.getAttribLocation(program, 'a_opacity');
  const aSize = gl.getAttribLocation(program, 'a_size');
  const aStrokeScale = gl.getAttribLocation(program, 'a_stroke_scale');

  const uMatrix = gl.getUniformLocation(program, 'u_matrix');
  const uResolution = gl.getUniformLocation(program, 'u_resolution');
  const uPixelRatio = gl.getUniformLocation(program, 'u_pixel_ratio');
  const uStrokeWidth = gl.getUniformLocation(program, 'u_stroke_width');

  if (
    aPosition < 0 ||
    aCorner < 0 ||
    aFill < 0 ||
    aStroke < 0 ||
    aOpacity < 0 ||
    aSize < 0 ||
    aStrokeScale < 0 ||
    !uMatrix ||
    !uResolution ||
    !uPixelRatio ||
    !uStrokeWidth
  ) {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    throw new Error('Failed to resolve point shader locations');
  }

  return {
    program,
    buffer,
    aPosition,
    aCorner,
    aFill,
    aStroke,
    aOpacity,
    aSize,
    aStrokeScale,
    uMatrix,
    uResolution,
    uPixelRatio,
    uStrokeWidth,
  };
}
